import type { ModalProps } from '@mantine/core';
import { Box, Group, Tabs, useMantineTheme } from '@mantine/core';
import type { TeamOut } from '@repo/api-codegen';
import { IconWrapper } from '@repo/common/components/IconWrapper';
import { Badge, Text } from '@repo/foundations';
import { useState } from 'react';
import { ScrollableModal } from '../ScrollableModal/ScrollableModal';
import { TeamGeneralTab } from './TeamGeneralTab';
import { TeamMembersTab } from './TeamMembersTab';
import { useStyle } from './TeamSettingsModal.styles';
import { TeamSidebarTab } from './TeamSidebarTab';

type Props = ModalProps & { team: TeamOut };

export function TeamSettingsModal({ opened, onClose, team }: Props) {
	const { classes } = useStyle();
	const theme = useMantineTheme();

	const [activeTab, setActiveTab] = useState<string | null>('general');

	return (
		<ScrollableModal
			opened={opened}
			onClose={onClose}
			size="lg"
			title={
				<Group spacing={8} align="center">
					<IconWrapper>{team.icon}</IconWrapper>
					<Text weight="bold">{team.name}</Text>
					{team.is_default_team && <Badge m={0}>Default Team</Badge>}
				</Group>
			}
		>
			<Box
				mx={`-${theme.spacing.md}`}
				px={theme.spacing.md}
				h={theme.other.space[160]}
				style={{
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
				}}
			>
				<Tabs
					keepMounted={false}
					defaultValue="general"
					classNames={classes}
					color="dark"
					value={activeTab}
					onTabChange={setActiveTab}
					style={{
						display: 'flex',
						flexDirection: 'column',
						height: '100%',
						flex: 1,
						minHeight: 0,
					}}
				>
					<Tabs.List>
						<Tabs.Tab value="general">
							<Text
								size="sm"
								weight="semibold"
								color={
									activeTab === 'general'
										? 'text/primary/default'
										: 'text/secondary/default'
								}
							>
								General
							</Text>
						</Tabs.Tab>
						<Tabs.Tab value="members">
							<Text
								size="sm"
								weight="semibold"
								color={
									activeTab === 'members'
										? 'text/primary/default'
										: 'text/secondary/default'
								}
							>
								Members
							</Text>
						</Tabs.Tab>
						<Tabs.Tab value="sidebar">
							<Text
								size="sm"
								weight="semibold"
								color={
									activeTab === 'sidebar'
										? 'text/primary/default'
										: 'text/secondary/default'
								}
							>
								Sidebar
							</Text>
						</Tabs.Tab>
					</Tabs.List>

					<Tabs.Panel value="general">
						<TeamGeneralTab team={team} />
					</Tabs.Panel>

					<Tabs.Panel value="members">
						<TeamMembersTab team={team} />
					</Tabs.Panel>

					<Tabs.Panel value="sidebar">
						<TeamSidebarTab team={team} />
					</Tabs.Panel>
				</Tabs>
			</Box>
		</ScrollableModal>
	);
}
